import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import * as Icons from 'lucide-react';
import { Mail, Phone, MapPin, ArrowRight } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { CONTACT_INFO, SOCIALS } from '../mock/mock';
import { getLandingLabels } from '../i18n/landing';
import { getOemLabel } from '../i18n/oem';
import { getDopamineLabel } from '../i18n/dopamine';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const labels = getLandingLabels(lang);
  const links = [
    { to: '/services', label: t('nav.services') },
    { to: '/private-label-cosmetics', label: labels.pl },
    { to: '/contract-cosmetics-manufacturing', label: labels.cm },
    { to: '/influencer-beauty-brand', label: labels.ib },
    { to: '/oem-skincare-manufacturer', label: getOemLabel(lang) },
    { to: '/dopamine', label: getDopamineLabel(lang) },
    { to: '/products', label: t('nav.products') },
    { to: '/rnd', label: t('nav.rnd') },
    { to: '/certifications', label: t('nav.certificates') },
    { to: '/faq', label: t('nav.faq') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const subscribe = async (e) => {
    e.preventDefault();
    setError('');
    setDone(false);
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      setDone(true);
      setEmail('');
      setTimeout(() => setDone(false), 5000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-espresso text-ivory/80">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div className="lg:col-span-1">
          <div className="flex flex-col leading-none mb-5">
            <span className="font-serif text-3xl font-semibold text-ivory">ARGE</span>
            <span className="text-[0.6rem] tracking-[0.4em] uppercase text-gold-light mt-1">Cosmetics</span>
          </div>
          <p className="text-sm leading-relaxed text-ivory/60">{t('footer.about')}</p>
          <div className="flex gap-3 mt-6">
            {SOCIALS.map((s) => {
              const Icon = Icons[s.icon] || Icons.Globe;
              return (
                <a key={s.name} href={s.url} target="_blank" rel="noreferrer" aria-label={s.name} className="w-9 h-9 flex items-center justify-center border border-ivory/20 hover:border-gold hover:text-gold-light transition-colors">
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        </div>

        <div>
          <h4 className="text-xs tracking-[0.22em] uppercase text-gold-light mb-5">{t('footer.quickLinks')}</h4>
          <ul className="space-y-3">
            {links.map((l) => (
              <li key={l.to}><Link to={l.to} className="text-sm text-ivory/70 hover:text-gold-light transition-colors">{l.label}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-xs tracking-[0.22em] uppercase text-gold-light mb-5">{t('footer.contactTitle')}</h4>
          <ul className="space-y-4 text-sm text-ivory/70">
            <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-0.5 text-gold-light shrink-0" /><span>{CONTACT_INFO.displayEmail}</span></li>
            <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-0.5 text-gold-light shrink-0" /><span>{CONTACT_INFO.phone}</span></li>
            <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-gold-light shrink-0" /><span>{CONTACT_INFO.addressLine}</span></li>
          </ul>
        </div>

        <div>
          <h4 className="text-xs tracking-[0.22em] uppercase text-gold-light mb-5">{t('footer.newsletter')}</h4>
          <form onSubmit={subscribe} data-testid="newsletter-form" className="flex border-b border-ivory/25 focus-within:border-gold transition-colors">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('footer.emailPlaceholder')}
              data-testid="newsletter-email-input"
              className="bg-transparent flex-1 py-2 text-sm text-ivory placeholder-ivory/40 focus:outline-none"
            />
            <button type="submit" disabled={loading} data-testid="newsletter-submit-button" className="px-2 text-gold-light hover:text-gold disabled:opacity-50"><ArrowRight className="w-5 h-5" /></button>
          </form>
          {done && <p data-testid="newsletter-success" className="text-xs text-gold-light mt-3">✓ {t('footer.subscribed')}</p>}
          {error && <p data-testid="newsletter-error" className="text-xs text-red-300 mt-3">{error}</p>}
        </div>
      </div>

      <div className="border-t border-ivory/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-ivory/50">© {new Date().getFullYear()} ARGE Cosmetics. {t('footer.rights')}</p>
          <p className="font-serif italic text-sm text-gold-light">{t('footer.tagline')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
